import { PowCaptcha } from "@yanzheng/pow";
import { ShieldCheck } from "lucide-react";

import { Button, Img, Input } from "../../components/ui.tsx";
import { PhoneInput } from "../../components/phone-input.tsx";
import { useAuthForm } from "../../lib/use-auth-form.ts";
import { useProducts } from "../../api/queries.ts";
import { useSiteConfig } from "../../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 登录 / 注册
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **左边这块不能讲平台怎么定价。**
 *    早先写的是"没登录看到的是平台指导零售价（最贵的一档）""绑定服务商之后
 *    按他给你的价算""钱压在平台不会先结给商家"—— 老板一眼看出问题：
 *    **那是我的运营模式，说出去我怎么赚钱。**
 *    而且对客户来说这几句还起反作用：等于当面告诉他"你现在被收的是最贵的价"。
 *
 *    对外只说**他能得到什么**（配齐、有人管、送到家、能退），
 *    不解释这些是怎么做到的。分销结构、指导价、结算时点都是内部机制。
 *
 * 🔴 **左边不能只有文字。**
 *    2560 的屏幕上一栏文字靠左贴边、中间大片空白 —— 老板的原话是
 *    「首页全是文字，文字还特别居左，中间很空」。
 *    所以：内容**限宽并居中**（不跟着屏幕无限拉宽），下面配一组真实商品图。
 *
 * 🔴 **但这里不列"我们会怎样"的好处。**
 *    曾经摆过三条（一站配齐 / 有人从头帮你到尾 / 大件送货上门，还写了
 *    "约时间、上楼层都可以先说好"）—— 老板的原话：
 *    **不要承诺一些我没有说过的事情。**
 *    送不送上门、售后是不是同一个人对接，是每个商家自己的事，平台没做过这个保证，
 *    写在登录页上等于平台替他们许诺了。图是事实，承诺不是。
 *
 * ⚠️ 商品图只是装饰，**不显示价格** —— 这一页还没有身份，
 *    显示任何价都会是给陌生人的那一档，正好又把机制说了一遍。
 * ⚠️ 取不到图就整块不出现，绝不留一排灰框（登录页出破图比没有图更糟）。
 */

export function DesktopLogin() {
  const a = useAuthForm();
  /**
   * 左边那块品牌区的三样东西（小标题 · 大标题 · 商品图）都从后台配
   * （「页面管理 → 登录页管理」）。以前是写死在这儿的，改一个字要发一次版。
   */
  const cfg = useSiteConfig();

  /**
   * ⚠️ 只拿图，不用价 —— 纯装饰，失败就不显示这一块。
   * ⚠️ **`manual` 也照样按销量拉一批再筛**，不给一个"按 id 取商品"的接口：
   *    多一条公开接口就多一处要考虑权限和上下架的地方，而这里只是装饰。
   *    挑中的商品要是下架了，它自然就不在这批里 —— 少一张图，不出破图。
   */
  const showcase = useProducts({ sort: "sales", limit: 48 }, cfg.loginShowcase !== "off");
  const items = showcase.data?.items ?? [];
  const pics = (
    cfg.loginShowcase === "manual"
      ? cfg.loginShowcaseProductIds
          .map((id) => items.find((p) => p.id === id))
          .filter(Boolean)
          .map((p) => p!.imageUrls?.[0])
      : items.map((p) => p.imageUrls?.[0])
  )
    .filter(Boolean)
    .slice(0, 6) as string[];

  return (
    /*
      ⚠️ **表单那栏固定 480，不跟屏幕对半分。**
         试过对半分，两块一样重，看着像两个页面拼在一起 —— 老板说很丑。
         登录页的主角是表单，它只需要那么宽；剩下的都给左边那块品牌区，
         中间不空是靠内容限宽居中 + 商品图撑起来的，不是靠把表单拉宽。
    */
    <div className="grid min-h-dvh grid-cols-[minmax(0,1fr)_480px] bg-muted/30">
      {/* ══════════ 左：这家商城能给你什么 ══════════ */}
      <div className="relative hidden flex-col justify-center overflow-hidden bg-gradient-to-br from-brand-600 to-brand-500 px-12 py-12 lg:flex">
        {/* ⚠️ 内容限宽并居中 —— 不限的话在宽屏上会贴着左边缘，右边一大片空。 */}
        <div className="mx-auto w-full max-w-[620px]">
          <p className="text-[15px] text-white/75">{cfg.loginEyebrow}</p>
          {/*
            ⚠️ `whitespace-pre-line` 才会把配置里的换行当换行渲染。
               不设的话两行会挤成一行，而这一句是**有意折行**的 ——
               交给浏览器自己折，屏宽一变断句就散了。
          */}
          <h1 className="mt-3 text-[34px] leading-tight font-medium whitespace-pre-line text-white">
            {cfg.loginHeadline}
          </h1>

          {/* ── 商品图拼贴：中间那片空白就是它该在的位置 ── */}
          {cfg.loginShowcase !== "off" && pics.length >= 3 && (
            <div className="mt-8 grid grid-cols-3 gap-3">
              {pics.map((src) => (
                <Img
                  key={src}
                  src={src}
                  alt=""
                  className="aspect-[4/3] w-full rounded-xl object-cover ring-1 ring-white/15"
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════════ 右：表单 ══════════ */}
      <div className="flex flex-col justify-center px-12">
        {/* ⚠️ 表单也限宽居中 —— 480 的栏里贴着边缘排也不好看。 */}
        <div className="mx-auto w-full max-w-[360px]">
          <h2 className="text-[24px] font-medium">{a.mode === "login" ? "登录" : "注册"}</h2>
          <p className="mt-1.5 text-[14px] text-muted-foreground">
            {a.mode === "login" ? "用手机号和密码登录" : "手机号注册，一步就好"}
          </p>

          {a.search.invite && a.mode === "register" && (
            <p className="mt-5 rounded-lg bg-brand-50 px-4 py-3 text-[14px] leading-relaxed text-brand-700">
              你是通过专属链接进来的，注册后将由该商家为你服务
            </p>
          )}

          <form onSubmit={a.submit} className="mt-6 space-y-4">
            {/*
              🔴 **注册时手机号带地区选择器**（老板指定：默认台湾 → 中国 → 其他），
                 存进库的是 E.164（`+8613700137000`）。
              ⚠️ **登录框仍然是一个纯文本框**，不给地区选择器：库里存量的老账号
                 是裸的 11 位（`13700137000`），而且大陆用户登录时就是习惯直接打
                 那 11 位数字。服务端用一组候选值去匹配（原样 / 正规化 / +86 / +886），
                 见 `auth/service.ts` 的 `identifierCandidates`。
                 这里加了选择器反而会把老用户挡在门外。
          */}
            {a.mode === "register" ? (
              <PhoneInput value={a.phone} onChange={a.setPhone} />
            ) : (
              <Input
                label="账号"
                autoComplete="username"
                placeholder="请输入手机号"
                value={a.phone}
                onChange={(e) => a.setPhone(e.target.value)}
              />
            )}
            <Input
              label="密码"
              type="password"
              autoComplete={a.mode === "login" ? "current-password" : "new-password"}
              placeholder={a.mode === "register" ? "至少 8 位" : "请输入密码"}
              value={a.password}
              onChange={(e) => a.setPassword(e.target.value)}
            />

            {/* 🔴 密码框是遮住的，打错一个字自己看不见 —— 而这个平台上账号是有钱的。 */}
            {a.mode === "register" && (
              <Input
                label="确认密码"
                type="password"
                autoComplete="new-password"
                placeholder="再输入一次"
                value={a.password2}
                onChange={(e) => a.setPassword2(e.target.value)}
              />
            )}
            {a.mode === "register" && (
              <Input
                label="昵称（选填）"
                value={a.nickname}
                onChange={(e) => a.setNickname(e.target.value)}
              />
            )}

            {a.showCaptcha && (
              <div className="rounded-lg bg-secondary/60 px-4 py-3">
                <p className="mb-2 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <ShieldCheck className="size-4" />
                  为了账号安全，请完成验证后重试
                </p>
                {/*
                  🔴 **注册页已经没有这一块了**（老板：「注册账号取消安全校验」）。
                     这里只剩**登录**那条路：服务端判定这个 IP 像在撞库时会回
                     `CAPTCHA_REQUIRED`，那时才补一道验证重试。
                     `showCaptcha` 现在就等于 `needCaptcha`，是服务端主动要的。
                  ⚠️ key 绑手机号：换号码必须重出一道题，否则票绑的还是旧号码。
                  ⚠️ 组件自己会算，用户零操作 —— 别加"点击验证"之类的引导。
                */}
                <PowCaptcha key={`${a.mode}-${a.phone}`} onSuccess={a.setCaptcha} />
              </div>
            )}

            {/*
              🔴 **错误提示不能把按钮挤下去。**
                 出错时按钮整块往下跳一截，用户正要点的位置突然变成了别的东西。
                 所以这里**预留一行的高度**：有没有错误，按钮都在同一个位置。
            */}
            <div className="min-h-[2.75rem]">
              {a.error && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-2.5">
                  <p className="text-[13px] leading-snug text-destructive break-anywhere">
                    {a.cooling
                      ? `尝试次数过多，请 ${a.left} 秒后再试`
                      : a.error instanceof Error
                        ? a.error.message
                        : "出了点问题"}
                  </p>
                </div>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={a.busy}
              disabled={a.cooling}
            >
              {a.cooling ? `${a.left} 秒后可重试` : a.mode === "login" ? "登录" : "注册并登录"}
            </Button>
          </form>

          <button
            className="mt-5 text-[14px] text-brand-600 transition-colors hover:text-brand-700"
            onClick={() => a.setMode(a.mode === "login" ? "register" : "login")}
          >
            {a.mode === "login" ? "还没有账号？去注册" : "已有账号？去登录"}
          </button>
        </div>
      </div>
    </div>
  );
}
